import React from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { H1, Text, H2 } from '../components/ui/Typography';
import { Card } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const Entrepreneur = () => {
  const { lang } = useParams();
  const isEn = lang === 'en';

  const content = {
    badge: isEn ? 'Preparation for business succession and continuity' : 'Zakelijke & Fiscale Continuïteit',
    title: isEn ? 'Estate Planning for Entrepreneurs & Directors' : 'Nalatenschap voor ondernemers en DGA’s',
    subtitle: isEn 
      ? 'Your estate impacts not only your family, but often your business as well. Discover with a free scan where the legal, fiscal, and strategic considerations lie.'
      : 'Uw nalatenschap raakt niet alleen uw familie, maar vaak ook uw onderneming. Ontdek met een gratis scan waar de fiscale en strategische aandachtspunten zitten.',
    cta: isEn ? 'Start Free Scan' : 'Start gratis scan',
    footer: isEn ? 'Preparation for business succession' : 'Voorbereiding op bedrijfsopvolging',
    section1Title: isEn ? 'Why a business-oriented approach is essential' : 'Waarom een zakelijke blik essentieel is',
    section1Text: isEn
      ? 'If your personal assets are intertwined with your company, standard inheritance law may not adequately address business continuity or fiscal structures. Proper preparation helps prevent uncertainty and financial complications.'
      : 'Als ondernemer is uw vermogen vaak verweven met uw bedrijf. De standaard wettelijke verdeling houdt geen rekening met stemrecht, continuïteit van de bedrijfsvoering of de specifieke fiscale faciliteiten (zoals de BOR) die voor u van cruciaal belang kunnen zijn.',
    risks: [
      { 
        title: isEn ? 'Business continuity' : 'Bedrijfscontinuïteit', 
        desc: isEn ? 'Unclear arrangements may threaten the continuity of your business in the event of illness or unexpected death.' : 'Onduidelijkheid kan de continuïteit van uw onderneming direct onder druk zetten bij een onverwacht overlijden.', 
        icon: 'Activity' 
      },
      { 
        title: isEn ? 'Fiscal opportunities' : 'Fiscale kansen', 
        desc: isEn ? 'Tax benefits and succession structures often remain unused without strategic preparation.' : 'Fiscale kansen rond bedrijfsopvolging blijven vaak onbenut zonder tijdige en strategische voorbereiding.', 
        icon: 'TrendingUp' 
      },
      { 
        title: isEn ? 'Asset structures' : 'Vermogens-structuur', 
        desc: isEn ? 'Private and business assets require an integrated and carefully structured approach.' : 'Privévermogen en ondernemingsvermogen vragen om een geïntegreerde en strategische benadering.', 
        icon: 'Shield' 
      }
    ],
    receiveTitle: isEn ? 'What you receive' : 'Wat u ontvangt',
    receiveText: isEn 
      ? 'After the free scan, you receive a strategic overview with the most important fiscal and legal considerations for your business structure and estate planning.'
      : 'Na de gratis scan ontvangt u een strategisch dossier met de belangrijkste fiscale en juridische aandachtspunten voor uw ondernemings- en privéstructuur.',
    points: isEn ? [
      'Insight into vulnerabilities within your asset and company structure',
      'Key considerations for tax optimization and business continuity',
      'Concrete follow-up steps for discussions with your notary or advisor'
    ] : [
      'Inzicht in kwetsbaarheden binnen uw vermogensstructuur',
      'Aandachtspunten rond fiscale optimalisatie en bedrijfscontinuïteit',
      'Concrete vervolgstappen voor het gesprek met uw notaris'
    ],
    stepsTitle: isEn ? 'Strategic preparation' : 'Strategische voorbereiding',
    stepsText: isEn 
      ? 'We translate complex business structures into clear and practical insights for your notary appointment.'
      : 'Wij vertalen complexe zakelijke structuren naar een heldere intake voor uw notaris.',
    steps: [
      { step: isEn ? '1. Analysis' : '1. Analyse', desc: isEn ? 'Assessment of your business and private asset structure through our scan.' : 'Inventarisatie van uw zakelijke en privé vermogensstructuur via onze scan.' },
      { step: isEn ? '2. Simulation' : '2. Simulatie', desc: isEn ? 'Identification of fiscal risks and optimization opportunities.' : 'Identificatie van fiscale risico\'s en optimalisatie-mogelijkheden.' },
      { step: isEn ? '3. Overview' : '3. Dossier', desc: isEn ? 'A notary-ready report that supports informed decisions regarding your estate.' : 'Een helder dossier met de noodzakelijke rechtskeuzes voor de notaris.' }
    ],
    finalTitle: isEn ? 'Prevent your estate from affecting your business' : 'Voorkom dat uw nalatenschap uw onderneming raakt',
    finalText: isEn 
      ? 'For entrepreneurs and directors, a standard approach is rarely sufficient. Start the free scan and discover which preparations best fit your situation.'
      : 'Voor ondernemers en DGA’s is een standaardaanpak zelden voldoende. Start de gratis scan en ontdek welke voorbereiding past bij uw situatie.'
  };

  return (
    <div className="w-full">
      <section className="relative pt-12 pb-24 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-50 rounded-full blur-3xl opacity-50 -z-10"></div>
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase tracking-widest mb-4">
              <SafeIcon name="Briefcase" className="text-zinc-400" />
              <span>{content.badge}</span>
            </div>
            <H1 className="text-gradient leading-tight">{content.title}</H1>
            <Text className="max-w-2xl mx-auto text-lg md:text-xl">{content.subtitle}</Text>
            <div className="flex flex-col items-center gap-6 pt-4">
              <Link to={`/${lang}/scan`}>
                <Button className="px-12 py-5 text-base shadow-xl">{content.cta}</Button>
              </Link>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">{content.footer}</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-zinc-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <H2 className="mb-6 text-2xl md:text-3xl">{content.section1Title}</H2>
          <Text className="text-slate-600">{content.section1Text}</Text>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {content.risks.map((risk, i) => (
              <Card key={i} className="p-8 border-zinc-100 shadow-sm" hover={true}>
                <div className="w-12 h-12 bg-zinc-50 rounded-xl flex items-center justify-center mb-6 text-navy-900">
                  <SafeIcon name={risk.icon} className="text-xl" />
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-3">{risk.title}</h3>
                <Text subtle className="text-sm leading-relaxed">{risk.desc}</Text>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-zinc-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-navy-900 mb-6">{content.receiveTitle}</h2>
              <Text className="mb-8">{content.receiveText}</Text>
              <ul className="space-y-4">
                {content.points.map((point, i) => (
                  <li key={i} className="flex items-center gap-3 text-zinc-900 font-semibold text-sm">
                    <div className="w-6 h-6 rounded-full bg-navy-900 flex items-center justify-center text-white shrink-0">
                      <SafeIcon name="Check" className="text-[10px]" />
                    </div>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <Card className="p-10 border-none shadow-2xl bg-white rounded-[2.5rem]">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">{isEn ? 'Strategic Analysis' : 'Strategische Analyse'}</div>
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold">{isEn ? 'Fiscal Optimization' : 'Fiscale Optimalisatie'}</span>
                    <span className="text-[10px] bg-green-50 text-green-700 px-2 py-0.5 rounded font-bold">{isEn ? 'High Potential' : 'Groot potentieel'}</span>
                  </div>
                  <div className="h-2 w-full bg-slate-100 rounded"></div>
                  <div className="h-2 w-4/6 bg-slate-100 rounded"></div>
                  <div className="pt-4 border-t border-slate-50">
                    <div className="text-[10px] font-bold text-slate-400 uppercase mb-2 tracking-widest">{isEn ? 'Continuity Score' : 'Continuïteitsscore'}</div>
                    <div className="text-2xl font-bold text-navy-900">7.2<span className="text-sm text-slate-300">/10</span></div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center mb-16">
          <h2 className="text-3xl font-bold text-navy-900 mb-4">{content.stepsTitle}</h2>
          <Text className="max-w-2xl mx-auto">{content.stepsText}</Text>
        </div>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {content.steps.map((s, i) => (
              <div key={i} className="space-y-4">
                <div className="text-navy-900 font-bold text-sm uppercase tracking-widest">{s.step}</div>
                <Text subtle className="text-sm">{s.desc}</Text>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 bg-navy-950 text-white text-center rounded-t-[3rem] relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint-pattern opacity-[0.03]"></div>
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <h2 className="text-3xl md:text-4xl font-semibold mb-6 tracking-tight">{content.finalTitle}</h2>
          <p className="text-slate-400 text-lg mb-10">{content.finalText}</p>
          <Link to={`/${lang}/scan`}>
            <Button variant="white" className="px-12 py-5 text-base">{content.cta}</Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Entrepreneur;